import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { useEffect, useState, type FC, type ReactNode, type Ref } from 'react';

import {
  Popover,
  PopoverLayover,
  PopoverTrigger,
} from '@/core/tamagoshi/Popover/Popover';
import { Text } from '@/core/tamagoshi/Text/Text';
import { InfoIcon } from '@/core/tamagoshiTailwind/icons/InfoIcon';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';

import type { FilterExplanationModalProps } from './components/FilterExplanationModal/FilterExplanationModal';
import { stopEventPropagation } from './stopEventPropagation';

import styles from './FilterExplanation.module.scss';

const DynamicFilterExplanationModal = dynamic<FilterExplanationModalProps>(
  () =>
    import('./components/FilterExplanationModal/FilterExplanationModal').then(
      (mod) => mod.FilterExplanationModal,
    ),
  { ssr: false },
);

export interface FilterExplanationProps {
  filterExplanationText: ReactNode;
  icon?: ReactNode;
  label?: string;
  attributeName?: string;
  className?: string;
  popoverClassName?: string;
}

export const FilterExplanation: FC<FilterExplanationProps> = ({
  filterExplanationText,
  icon,
  label,
  attributeName,
  className,
  popoverClassName,
}) => {
  const [isDesktop, setIsDesktop] = useState(false);

  const gtmClickOnExplanation = () =>
    Gtm.push({
      event: 'interaction_click on a link',
      e_category: 'navigation',
      e_action: 'Click on a link',
      e_label: 'Read more',
      amp_event_name: 'Click on a link',
      event_trigger: `${attributeName}_filter_link`,
      redirection_link: undefined,
      click_element: 'Read more',
      interaction_hit: 'False',
    });

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  const onPopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    stopEventPropagation(event);
    setIsPopoverVisible(true);
    gtmClickOnExplanation();
  };
  const onPopoverClose = () => {
    setIsPopoverVisible(false);
  };

  const [isModalVisible, setIsModalVisible] = useState(false);
  const onModalOpen = () => {
    setIsModalVisible(true);
    gtmClickOnExplanation();
  };
  const onModalClose = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    const mediaQueryList = window.matchMedia('(min-width: 1024px)');
    if (mediaQueryList.matches) {
      setIsDesktop(true);
    }

    const importFilters = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    if (mediaQueryList.addEventListener !== undefined) {
      mediaQueryList.addEventListener('change', importFilters);
    } else {
      mediaQueryList.addListener(importFilters);
    }

    return () => {
      if (mediaQueryList.removeEventListener !== undefined) {
        mediaQueryList.removeEventListener('change', importFilters);
      } else {
        mediaQueryList.removeListener(importFilters);
      }
    };
  }, [setIsDesktop]);

  return (
    <>
      {isDesktop ? (
        <div className={classnames(styles.explanationContainer, className)}>
          <Popover
            position="right"
            fallbackPosition="bottom"
            isVisible={isPopoverVisible}
            onClose={onPopoverClose}
            onClick={stopEventPropagation}
            className={popoverClassName}
          >
            <PopoverTrigger>
              {({ ref }) => (
                <Text
                  className={styles.knowMoreText}
                  onClick={onPopoverOpen}
                  ref={ref as Ref<HTMLDivElement>}
                >
                  <InfoIcon className="size-[16px] text-foundation-primary" />
                </Text>
              )}
            </PopoverTrigger>
            <PopoverLayover>
              <Text>{filterExplanationText}</Text>
            </PopoverLayover>
          </Popover>
        </div>
      ) : (
        <div
          className={classnames(styles.explanationContainer, className)}
          onClick={stopEventPropagation}
          aria-hidden
        >
          <Text className={styles.knowMoreText} onClick={onModalOpen}>
            <InfoIcon className="size-[16px] text-foundation-primary" />
          </Text>
          {isModalVisible && (
            <DynamicFilterExplanationModal
              filterExplanationText={filterExplanationText}
              onClose={onModalClose}
              icon={icon}
              label={label}
            />
          )}
        </div>
      )}
    </>
  );
};
